import { useMemo } from 'react'
import { useAppSelector } from 'src/redux/storeHooks'
import { isImpersonationSessionActive } from 'src/utils/impersonationTokens'

export type CustomerAccessLevel = 'SUPERADMIN' | 'WRITE' | 'READ' | 'RESTRICT' | null

export interface CustomerAccessControl {
  // Access level
  accessLevel: CustomerAccessLevel
  
  // Permission checks
  canView: boolean
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
  canManage: boolean // Full management (SUPERADMIN only)
  
  // Customer allocation checks
  hasAccessToCustomer: (customerId: number | string | null | undefined) => boolean
  hasAccessToAllCustomers: boolean
  
  // Helper flags
  isSuperAdmin: boolean
  isReadOnly: boolean
  isRestricted: boolean
  
  // Legacy support - for backward compatibility
  normalizedCustomerAccess: CustomerAccessLevel
}

/**
 * Centralized hook for customer access control
 * 
 * Behavior:
 * - Direct customer login (no parent_user_role): Grants full access to all modules without restrictions
 * - Admin logged in as customer (has parent_user_role): Applies access control based on admin's permissions
 * 
 * Handles SUPERADMIN, WRITE, READ, and RESTRICT permissions
 * Supports customer allocation checks for admin impersonation scenarios
 */
export const useCustomerAccess = (): CustomerAccessControl => {
  const { fetchedUserAccountData } = useAppSelector((state) => state.userAccountData)
  
  return useMemo(() => {
    // When admin logs in as customer (parent_user_role exists), grant full customer access
    // This allows them to perform all customer actions
    const isLoggedInAsCustomer =
      !!fetchedUserAccountData?.parent_user_role || isImpersonationSessionActive()
    const isDirectCustomerLogin = fetchedUserAccountData?.user_type === 'USER' && !fetchedUserAccountData?.parent_user_role
    
    // Direct customer login OR admin logged in as customer - grant full access
    // Both scenarios should have full customer access
    if (isDirectCustomerLogin || isLoggedInAsCustomer) {
      // For direct customer login (user_type === 'USER' and no parent_user_role) 
      // OR admin logged in as customer (has parent_user_role)
      // Grant full customer access
      return {
        accessLevel: 'SUPERADMIN' as CustomerAccessLevel,
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canManage: true,
        hasAccessToCustomer: () => true,
        hasAccessToAllCustomers: true,
        isSuperAdmin: true,
        isReadOnly: false,
        isRestricted: false,
        normalizedCustomerAccess: 'SUPERADMIN' as CustomerAccessLevel,
      }
    }
    
    // Direct admin login (no parent_user_role and user_type === 'SUPERADMIN') - apply access control based on admin's permissions
    const isSuperUser = fetchedUserAccountData?.is_superuser || 
                       fetchedUserAccountData?.user_type === 'SUPERADMIN'
    
    // When logged in as customer, check parent_user_role (admin's role)
    const roleToCheck = fetchedUserAccountData?.parent_user_role
    
    // Check both customer_access and user_access for SUPERADMIN
    // user_access='SUPERADMIN' should grant super admin access to customer modules
    const customerAccess = roleToCheck?.customer_access?.toUpperCase() as CustomerAccessLevel
    const userAccess = roleToCheck?.user_access?.toUpperCase()
    const normalizedAccess = customerAccess || (userAccess === 'SUPERADMIN' ? 'SUPERADMIN' : null)
    
    // Get allocated customer IDs
    // Support both string (comma-separated) and array formats
    let allocatedCustomerIds: number[] = []
    
    if (fetchedUserAccountData?.customer_ids) {
      if (typeof fetchedUserAccountData.customer_ids === 'string') {
        allocatedCustomerIds = fetchedUserAccountData.customer_ids
          .split(',')
          .map(id => Number(id.trim()))
          .filter(id => !isNaN(id) && id > 0)
      } else if (Array.isArray(fetchedUserAccountData.customer_ids)) {
        allocatedCustomerIds = fetchedUserAccountData.customer_ids
          .map(id => typeof id === 'string' ? Number(id) : id)
          .filter(id => !isNaN(id as number) && (id as number) > 0) as number[]
      }
    }
    
    // Check if user has access to all customers
    // Empty array or null means all customers
    const hasAllCustomers = allocatedCustomerIds.length === 0 || 
                           allocatedCustomerIds.includes(0) ||
                           allocatedCustomerIds.includes(null as any)
    
    // Determine permissions based on access level
    // Also check user_access='SUPERADMIN' from parent_user_role (when logged in as customer)
    const isSuperAdminAccess = isSuperUser || 
                              normalizedAccess === 'SUPERADMIN' ||
                              userAccess === 'SUPERADMIN'
    const isWriteAccess = normalizedAccess === 'WRITE'
    const isReadAccess = normalizedAccess === 'READ'
    const isRestricted = normalizedAccess === 'RESTRICT'
    
    // Permission flags
    // View: SUPERADMIN, WRITE, or READ (not RESTRICT)
    const canView = !isRestricted && (isSuperAdminAccess || isWriteAccess || isReadAccess)
    
    // Create: SUPERADMIN or WRITE (with customer allocation check)
    const canCreate = !isRestricted && (isSuperAdminAccess || isWriteAccess)
    
    // Edit: SUPERADMIN or WRITE (with customer allocation check)
    const canEdit = !isRestricted && (isSuperAdminAccess || isWriteAccess)
    
    // Delete: Only SUPERADMIN
    const canDelete = !isRestricted && isSuperAdminAccess
    
    // Manage: Full management access (SUPERADMIN only)
    const canManage = isSuperAdminAccess
    
    // Check if user has access to a specific customer
    const hasAccessToCustomer = (customerId: number | string | null | undefined): boolean => {
      if (!customerId) return false
      
      // SUPERADMIN has access to all customers
      if (isSuperAdminAccess) return true
      
      // If no access, return false
      if (isRestricted || !canView) return false
      
      // If has all customers or customer is in allocated list
      const customerIdNum = typeof customerId === 'string' ? Number(customerId) : customerId
      if (isNaN(customerIdNum)) return false
      
      return hasAllCustomers || allocatedCustomerIds.includes(customerIdNum)
    }
    
    return {
      accessLevel: normalizedAccess,
      canView,
      canCreate,
      canEdit,
      canDelete,
      canManage,
      hasAccessToCustomer,
      hasAccessToAllCustomers: isSuperAdminAccess || hasAllCustomers,
      isSuperAdmin: isSuperAdminAccess,
      isReadOnly: isReadAccess,
      isRestricted,
      normalizedCustomerAccess: normalizedAccess,
    }
  }, [fetchedUserAccountData])
}

